/**
 * FlyerPage — /flyer
 *
 * Screen preview + one-click print for the Grannäventyret A6 flyer.
 * No auth required — anyone can print and share the flyer.
 *
 * Printing tips shown to user:
 *   • Set paper size to A6 landscape in the print dialog, OR
 *   • Print on A4 and select "2 pages per sheet" to get two A6 flyers.
 */

import { useState, useEffect } from 'react'
import QRCode from 'qrcode'
import GrannaventyretFlyer from '../components/GrannaventyretFlyer.jsx'

const FLYER_URL = 'https://grannaventyret.se'

export default function FlyerPage() {
  const [qrDataUrl, setQrDataUrl] = useState('')
  const [qrError, setQrError]   = useState(false)

  /* Generate QR once on mount — black modules on white for scan reliability */
  useEffect(() => {
    QRCode.toDataURL(FLYER_URL, {
      width:                 420,   /* generous resolution for print */
      margin:                2,
      errorCorrectionLevel: 'M',   /* M = ~15 % recovery — good balance */
      color: {
        dark:  '#000000',
        light: '#ffffff',
      },
    })
      .then(setQrDataUrl)
      .catch(() => setQrError(true))
  }, [])

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center py-10 px-4 gap-6">

      {/* ── Screen header ────────────────────────────────────────────── */}
      <div className="ga-no-print text-center max-w-sm">
        <div className="inline-flex items-center gap-2 mb-3">
          <span className="text-2xl">🗺️</span>
          <h1 className="text-xl font-bold text-indigo-700 tracking-tight">
            Reklamblad — A6 liggande
          </h1>
        </div>
        <p className="text-sm text-slate-500 leading-relaxed">
          Skriv ut och dela med grannar. Välj <strong>A6 liggande</strong> i
          skrivardialogen, eller <strong>"2 sidor per ark"</strong> på A4 för
          att få två blad per utskrift.
        </p>
      </div>

      {/* ── Action buttons ───────────────────────────────────────────── */}
      <div className="ga-no-print flex flex-wrap items-center justify-center gap-3">
        <button
          onClick={() => window.print()}
          disabled={!qrDataUrl}
          className="
            inline-flex items-center gap-2
            rounded-xl bg-indigo-600 px-6 py-2.5
            text-sm font-semibold text-white
            shadow-md shadow-indigo-200
            hover:bg-indigo-700 active:scale-95
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-all
          "
        >
          🖨️ Skriv ut
        </button>

        {qrDataUrl && (
          <a
            href={qrDataUrl}
            download="grannaventyret-qr.png"
            className="
              inline-flex items-center gap-2
              rounded-xl border border-slate-300 bg-white px-5 py-2.5
              text-sm font-medium text-slate-600
              hover:bg-slate-50 active:scale-95
              transition-all
            "
          >
            ⬇️ Ladda ned QR-kod
          </a>
        )}
      </div>

      {/* ── Error notice ─────────────────────────────────────────────── */}
      {qrError && (
        <p className="ga-no-print text-xs text-red-500 text-center">
          Kunde inte generera QR-kod — kontrollera internetanslutningen och ladda om sidan.
        </p>
      )}

      {/* ── Flyer — visible on screen AND in print ───────────────────── */}
      <div
        style={{
          boxShadow: '0 12px 40px rgba(79, 70, 229, 0.18), 0 2px 8px rgba(0,0,0,0.08)',
          borderRadius: '12px',
          overflow: 'hidden',
          transformOrigin: 'top center',
          maxWidth: '100%',
        }}
      >
        <GrannaventyretFlyer qrDataUrl={qrDataUrl} />
      </div>

      {/* ── Tips footer ──────────────────────────────────────────────── */}
      <div className="ga-no-print mt-2 rounded-xl bg-indigo-50 border border-indigo-100 p-4 max-w-sm w-full text-xs text-indigo-700 space-y-1">
        <p className="font-semibold text-indigo-800 mb-1">💡 Utskriftstips</p>
        <p>• Välj <strong>A6 liggande</strong> som pappersstorlek om din skrivare stöder det.</p>
        <p>• Annars: skriv ut på A4 och välj <strong>2 sidor per ark</strong> i skrivarens inställningar.</p>
        <p>• Stäng av <strong>skalning</strong> ("Fit to page") för exakta marginaler.</p>
        <p>• QR-koden fungerar med alla mobilkameror — ingen app krävs för att skanna.</p>
      </div>
    </div>
  )
}
